import { useState, useEffect, useCallback } from 'react';
import { 
    MessageCircle, 
    ThumbsUp, 
    Reply, 
    MoreHorizontal, 
    Pin, 
    Send,
    Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { API_URL as API } from '../config';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Comment = ({ comment, postId, onReplyAdded, onPinned, isPostAuthor }) => {
    const { user } = useAuth();
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [liked, setLiked] = useState(!!comment.user_reaction);
    const [likesCount, setLikesCount] = useState(comment.reactions?.like || 0);

    const handleReact = async () => {
        try {
            const response = await axios.post(`${API}/interactions/react/${comment.id}?target_type=comment`, { reaction_type: 'like' });
            setLiked(response.data.action === 'added' || response.data.action === 'changed');
            setLikesCount(prev => response.data.action === 'added' ? prev + 1 : (response.data.action === 'removed' ? Math.max(0, prev - 1) : prev));
        } catch (error) {
            toast.error('Failed to react to comment');
        }
    };

    const handleReply = async () => {
        if (!replyContent.trim()) return;
        setLoading(true);
        try {
            await axios.post(`${API}/posts/${postId}/comments`, {
                content: replyContent,
                parent_id: comment.id
            });
            setReplyContent('');
            setShowReplyInput(false);
            if (onReplyAdded) onReplyAdded();
            toast.success('Reply added');
        } catch (error) {
            toast.error('Failed to add reply');
        } finally {
            setLoading(false);
        }
    };

    const handlePin = async () => {
        try {
            await axios.post(`${API}/interactions/pin-comment/${postId}/${comment.id}`);
            if (onPinned) onPinned();
            toast.success('Comment pinned');
        } catch (error) {
            toast.error('Failed to pin comment');
        }
    };

    return (
        <div className="flex gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold text-zinc-500 overflow-hidden shrink-0">
                {comment.author_avatar ? <img src={comment.author_avatar} alt="" /> : comment.author_name?.charAt(0)}
            </div>
            <div className="flex-grow min-w-0">
                <div className="bg-zinc-900 rounded-2xl px-4 py-2 inline-block max-w-full">
                    <div className="flex items-center justify-between gap-4 mb-1">
                        <span className="text-xs font-bold text-zinc-300">{comment.author_name}</span>
                        {comment.is_pinned && <span className="text-[10px] text-inclusion-gold flex items-center gap-1"><Pin className="w-2.5 h-2.5" /> Pinned</span>}
                    </div>
                    <p className="text-xs text-zinc-400 break-words">{comment.content}</p>
                </div>
                
                <div className="flex items-center gap-4 mt-1 px-2">
                    <button 
                        className={`text-[10px] font-bold ${liked ? 'text-inclusion-gold' : 'text-zinc-500'} hover:underline`}
                        onClick={handleReact}
                    >
                        Like {likesCount > 0 && `(${likesCount})`}
                    </button>
                    {!comment.parent_id && (
                        <button 
                            className="text-[10px] font-bold text-zinc-500 hover:underline"
                            onClick={() => setShowReplyInput(!showReplyInput)}
                        >
                            Reply
                        </button>
                    )}
                    <span className="text-[10px] text-zinc-600">{formatDistanceToNow(new Date(comment.created_at))} ago</span>
                    
                    {isPostAuthor && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="text-zinc-600 hover:text-zinc-400 p-1"><MoreHorizontal className="w-3 h-3" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-zinc-800 border-zinc-700 text-zinc-300">
                                <DropdownMenuItem className="gap-2 cursor-pointer text-xs" onClick={handlePin}>
                                    <Pin className="w-3 h-3" /> {comment.is_pinned ? 'Unpin' : 'Pin comment'}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                {showReplyInput && (
                    <div className="mt-3 flex items-center gap-2">
                        <Input 
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            placeholder="Write a reply..."
                            className="h-8 bg-zinc-800 border-zinc-700 text-xs text-zinc-300"
                        />
                        <Button 
                            size="sm" 
                            className="h-8 bg-inclusion-gold text-black"
                            onClick={handleReply}
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        </Button>
                    </div>
                )}

                {/* Nested Replies */}
                {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-4 space-y-4">
                        {comment.replies.map(reply => (
                            <Comment 
                                key={reply.id} 
                                comment={reply} 
                                postId={postId} 
                                onReplyAdded={onReplyAdded} 
                                isPostAuthor={isPostAuthor}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const CommentSection = ({ postId, isPostAuthor }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const fetchComments = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/posts/${postId}/comments`);
            setComments(response.data);
        } catch (error) {
            console.error('Failed to fetch comments:', error);
        } finally {
            setFetching(false);
        }
    }, [postId]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleSubmit = async () => {
        if (!newComment.trim()) return;
        setLoading(true);
        try {
            await axios.post(`${API}/posts/${postId}/comments`, {
                content: newComment
            });
            setNewComment('');
            fetchComments();
            toast.success('Comment added');
        } catch (error) {
            toast.error('Failed to add comment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-zinc-800 shrink-0" />
                <div className="relative flex-grow">
                    <Input 
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="bg-zinc-900 border-zinc-800 pr-12 focus-visible:ring-inclusion-gold/30 text-zinc-300"
                    />
                    <Button 
                        size="icon" 
                        variant="ghost" 
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-inclusion-gold hover:text-inclusion-gold hover:bg-transparent"
                        onClick={handleSubmit}
                        disabled={loading || !newComment.trim()}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                </div>
            </div>

            {fetching ? (
                <div className="flex justify-center p-4">
                    <Loader2 className="w-6 h-6 animate-spin text-zinc-700" />
                </div>
            ) : (
                <div className="space-y-2">
                    {comments.map(comment => (
                        <Comment 
                            key={comment.id} 
                            comment={comment} 
                            postId={postId} 
                            onReplyAdded={fetchComments}
                            onPinned={fetchComments}
                            isPostAuthor={isPostAuthor}
                        />
                    ))}
                    {comments.length === 0 && (
                        <p className="text-center text-xs text-zinc-500 py-4 italic">No comments yet. Be the first to share your thoughts!</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default CommentSection;
